'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Script from 'next/script';
import { authApi } from '@/lib/api/auth.api';
import { useAuth } from '@/hooks/useAuth';

interface GoogleAuthButtonProps {
  role: 'tourist' | 'operator';
  intent?: 'login' | 'signup';
  disabled?: boolean;
  prompt?: boolean;
  active?: boolean;
  onSuccess?: () => void;
  onError?: (message: string | null) => void;
}

export default function GoogleAuthButton({
  role,
  intent = 'login',
  disabled = false,
  prompt = true,
  active = true,
  onSuccess,
  onError,
}: GoogleAuthButtonProps) {
  const { authenticateWithGoogle } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const buttonText = useMemo(() => {
    if (intent === 'signup') {
      return 'signup_with' as const;
    }
    return 'continue_with' as const;
  }, [intent]);

  const handleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      if (!response.credential || isBusy) {
        return;
      }

      setIsBusy(true);
      onError?.(null);

      try {
        const result = await authenticateWithGoogle(role, response.credential);
        if (result.success) {
          onSuccess?.();
        } else {
          onError?.(result.error?.message || 'Google sign-in failed. Please try again.');
        }
      } catch (error) {
        onError?.(
          error instanceof Error
            ? error.message
            : 'Google sign-in failed. Please try again.'
        );
      } finally {
        setIsBusy(false);
      }
    },
    [authenticateWithGoogle, isBusy, onError, onSuccess, role]
  );

  useEffect(() => {
    let isMounted = true;

    authApi.getGoogleClientConfig().then((response) => {
      if (!isMounted) {
        return;
      }

      if (response.success && response.data?.clientId) {
        setClientId(response.data.clientId);
      } else if (response.error?.message) {
        onError?.(response.error.message);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [onError]);

  useEffect(() => {
    if (!active || disabled || !scriptLoaded || !clientId || !containerRef.current || !window.google?.accounts?.id) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCredential,
      auto_select: false,
      cancel_on_tap_outside: true,
      context: intent === 'signup' ? 'signup' : 'signin',
      ux_mode: 'popup',
    });

    containerRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(containerRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: buttonText,
      shape: 'pill',
      width: 320,
      logo_alignment: 'left',
    });

    initializedRef.current = true;

    if (prompt) {
      window.google.accounts.id.prompt();
    }

    return () => {
      if (initializedRef.current && window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [active, buttonText, clientId, disabled, handleCredential, intent, prompt, scriptLoaded]);

  return (
    <div data-component-id="src/components/auth/GoogleAuthButton.tsx:129:4" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="129" data-component-end-line="152" data-component-file="GoogleAuthButton.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
      <Script data-component-id="src/components/auth/GoogleAuthButton.tsx:130:6" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="130" data-component-end-line="134" data-component-file="GoogleAuthButton.tsx" data-component-name="Script" data-component-content="%7B%22elementName%22%3A%22Script%22%2C%22src%22%3A%22https%3A%2F%2Faccounts.google.com%2Fgsi%2Fclient%22%7D"
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
        onLoad={() => setScriptLoaded(true)}
      />
      <div data-component-id="src/components/auth/GoogleAuthButton.tsx:135:6" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="135" data-component-end-line="148" data-component-file="GoogleAuthButton.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
        <div data-component-id="src/components/auth/GoogleAuthButton.tsx:136:8" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="136" data-component-end-line="139" data-component-file="GoogleAuthButton.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          ref={containerRef}
          className={`flex min-h-[44px] items-center justify-center overflow-hidden rounded-full ${disabled ? 'pointer-events-none opacity-60' : ''}`}
        />
        {isBusy && (
          <div data-component-id="src/components/auth/GoogleAuthButton.tsx:141:10" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="141" data-component-end-line="146" data-component-file="GoogleAuthButton.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%20rounded-full%20border%20border-slate-200%20bg-white%2F95%22%7D" className="absolute inset-0 flex items-center justify-center rounded-full border border-slate-200 bg-white/95">
            <div data-component-id="src/components/auth/GoogleAuthButton.tsx:142:12" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="142" data-component-end-line="145" data-component-file="GoogleAuthButton.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20text-sm%20font-medium%20text-slate-600%22%7D" className="flex items-center space-x-2 text-sm font-medium text-slate-600">
              <div data-component-id="src/components/auth/GoogleAuthButton.tsx:143:14" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="143" data-component-end-line="143" data-component-file="GoogleAuthButton.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20w-4%20rounded-full%20border-2%20border-slate-300%20border-t-slate-700%20animate-spin%22%7D" className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
              <span data-component-id="src/components/auth/GoogleAuthButton.tsx:144:14" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="144" data-component-end-line="144" data-component-file="GoogleAuthButton.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Signing%20you%20in...%22%7D">Signing you in...</span>
            </div>
          </div>
        )}
      </div>
      {isBusy && (
        <p data-component-id="src/components/auth/GoogleAuthButton.tsx:150:8" data-component-path="src/components/auth/GoogleAuthButton.tsx" data-component-line="150" data-component-end-line="150" data-component-file="GoogleAuthButton.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-center%20text-xs%20text-slate-500%22%2C%22textContent%22%3A%22Signing%20you%20in%20with%20Google...%22%7D" className="text-center text-xs text-slate-500">Signing you in with Google...</p>
      )}
    </div>
  );
}
